import React, { useState, useEffect, useRef } from 'react';
import { Wrench, ShieldCheck } from 'lucide-react';
import { useAuth } from '../store/auth';
import { Field, Input, Select, ErrorMsg } from '../components/UI';
import { isValidEmail, isValidPassword, isValidName, sanitizeText } from '../lib/validation';
import { FYLKER, KOMMUNER_BY_FYLKE } from '../lib/norwayGeo';
import { PersonvernPage } from './PersonvernPage';

type Mode = 'login' | 'register' | 'forgot' | 'mfa';

const pillBtn = (extra?: React.CSSProperties): React.CSSProperties => ({
  width: '100%', height: 54,
  borderRadius: 999,
  background: 'var(--green)',
  color: '#fff',
  fontSize: 16, fontWeight: 700,
  border: 'none', cursor: 'pointer',
  marginTop: 8,
  letterSpacing: 0.1,
  ...extra,
});

const tabBtn = (active: boolean): React.CSSProperties => ({
  flex: 1, padding: '10px 0',
  border: 'none', borderRadius: 999,
  background: active ? 'var(--surface)' : 'transparent',
  fontSize: 14, fontWeight: 600,
  color: active ? 'var(--text)' : 'var(--text3)',
  boxShadow: active ? '0 1px 6px rgba(0,0,0,0.09)' : 'none',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
});

export const AuthPage: React.FC<{ recoveryMsg?: { ok: boolean; msg: string } | null }> = ({ recoveryMsg }) => {
  const { signIn, signUp, signOut, resetPassword, updatePassword, verifyMfa, sendMfaRecovery, mfaRequired, passwordRecovery } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [fullName, setFullName] = useState('');
  const [kommune, setKommune] = useState('');
  const [fylke, setFylke] = useState('');
  const [showPersonvern, setShowPersonvern] = useState(false);
  const [email, setEmail] = useState(() => sessionStorage.getItem('pendingInviteEmail') || '');
  const [password, setPassword] = useState('');
  const hasPendingInvite = !!sessionStorage.getItem('pendingInvite');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  // Sett nytt passord (etter klikk på gjenopprettingslenke)
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  // MFA
  const [mfaCode, setMfaCode] = useState('');
  const mfaInputRef = useRef<HTMLInputElement>(null);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoverySending, setRecoverySending] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  // Bytt til MFA-skjerm hvis store sier det trengs, eller tilbake til login
  useEffect(() => {
    if (mfaRequired) {
      setMode('mfa');
      setError('');
      setTimeout(() => mfaInputRef.current?.focus(), 100);
    } else {
      setMode(prev => prev === 'mfa' ? 'login' : prev);
    }
  }, [mfaRequired]);

  const handleMfaChange = (val: string) => {
    const clean = val.replace(/\D/g, '').slice(0, 6);
    setMfaCode(clean);
    if (clean.length === 6) handleVerifyMfa(clean);
  };

  const handleVerifyMfa = async (code: string) => {
    setLoading(true); setError('');
    const err = await verifyMfa(code, rememberDevice);
    if (err) { setError(err); setMfaCode(''); }
    setLoading(false);
  };

  const handleSetNewPassword = async () => {
    setResetError('');
    const pwCheck = isValidPassword(newPassword);
    if (!pwCheck.ok) { setResetError(pwCheck.message); return; }
    if (newPassword !== newPasswordConfirm) { setResetError('Passordene stemmer ikke overens'); return; }
    setResetSaving(true);
    const err = await updatePassword(newPassword);
    if (err) setResetError(err);
    setResetSaving(false);
  };

  const handleSendRecovery = async () => {
    setRecoverySending(true); setRecoveryError('');
    const err = await sendMfaRecovery();
    if (err) setRecoveryError(err);
    else setRecoverySent(true);
    setRecoverySending(false);
  };

  const handleSubmit = async () => {
    setError(''); setInfo('');

    if (mode === 'forgot') {
      if (!email) { setError('Fyll inn e-postadressen din'); return; }
      if (!isValidEmail(email)) { setError('Ugyldig e-postadresse'); return; }
      setLoading(true);
      const err = await resetPassword(email);
      if (err) setError(err);
      else setInfo('Sjekk e-posten din for en lenke til å tilbakestille passordet.');
      setLoading(false);
      return;
    }

    if (!email || !password) { setError('Fyll inn e-post og passord'); return; }
    if (!isValidEmail(email)) { setError('Ugyldig e-postadresse'); return; }

    if (mode === 'register') {
      const nameCheck = isValidName(fullName);
      if (!nameCheck.ok) { setError(nameCheck.message); return; }
      const pwCheck = isValidPassword(password);
      if (!pwCheck.ok) { setError(pwCheck.message); return; }
      if (password !== confirm) { setError('Passordene stemmer ikke overens'); return; }
      if (!fylke) { setError('Velg fylke'); return; }
      if (!kommune) { setError('Velg kommune'); return; }
    }

    if (mode === 'login' && password.length > 128) { setError('Ugyldig passord'); return; }

    setLoading(true);
    if (mode === 'login') {
      const err = await signIn(email, password);
      if (err) setError('Feil e-post eller passord');
    } else {
      const pendingInvite = sessionStorage.getItem('pendingInvite');
      const redirectTo = pendingInvite ? `https://app.toolshare.no?invite=${pendingInvite}` : undefined;
      const err = await signUp(email, password, sanitizeText(fullName, 100), redirectTo, kommune, fylke);
      if (err) setError(err);
      else setInfo('Sjekk e-posten din for å bekrefte kontoen, deretter kan du logge inn.');
    }
    setLoading(false);
  };

  const page: React.CSSProperties = {
    display: 'flex', flexDirection: 'column',
    height: '100%', overflowY: 'auto',
    background: 'var(--bg)',
    paddingTop: 'var(--safe-top)',
    paddingBottom: 'var(--safe-bottom)',
  };

  // Sett nytt passord etter klikk på gjenopprettingslenke i e-post.
  // Vises FØR MFA-sjekken under, men MFA-skjermen (om 2FA er aktivert) tar over
  // automatisk etter lagring, siden mfaRequired da blir satt til true.
  if (passwordRecovery && !mfaRequired) return (
    <div style={page}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '64px 24px 48px' }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.6, marginBottom: 8, color: 'var(--text)' }}>Sett nytt passord</h2>
        <p style={{ fontSize: 15, color: 'var(--text2)', marginBottom: 28, lineHeight: 1.6 }}>
          Skriv inn et nytt passord for kontoen din.
        </p>
        {resetError && <ErrorMsg msg={resetError} />}
        <Field label="Nytt passord">
          <Input type="password" placeholder="Minst 8 tegn med tall og bokstav" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSetNewPassword()} />
        </Field>
        <Field label="Bekreft nytt passord">
          <Input type="password" placeholder="Gjenta passordet" value={newPasswordConfirm} onChange={e => setNewPasswordConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSetNewPassword()} />
        </Field>
        <button onClick={handleSetNewPassword} disabled={resetSaving} style={pillBtn({ opacity: resetSaving ? 0.6 : 1 })}>
          {resetSaving ? 'Lagrer…' : 'Lagre nytt passord'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button
            onClick={() => signOut()}
            style={{ background: 'none', border: 'none', fontSize: 14, color: 'var(--text3)', cursor: 'pointer' }}
          >
            ← Avbryt og gå til innlogging
          </button>
        </div>
      </div>
    </div>
  );

  // MFA-verifiseringsskjerm
  if (mode === 'mfa') return (
    <div style={page}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 24px 48px', textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24, boxShadow: '0 8px 28px var(--green-glow)' }}>
          <ShieldCheck size={34} color="#fff" strokeWidth={2.2} />
        </div>
        <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.6, marginBottom: 8, color: 'var(--text)' }}>
          2-faktor verifisering
        </h2>
        <p style={{ fontSize: 15, color: 'var(--text2)', marginBottom: 36, lineHeight: 1.6, maxWidth: 300 }}>
          Åpne autentiseringsappen din og skriv inn den 6-sifrede koden.
        </p>

        {error && <ErrorMsg msg={error} />}

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 20, userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={e => setRememberDevice(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: 'var(--green)', cursor: 'pointer', flexShrink: 0 }}
          />
          <span style={{ fontSize: 14, color: 'var(--text2)' }}>Husk denne enheten i 30 dager</span>
        </label>

        <input
          ref={mfaInputRef}
          type="tel"
          inputMode="numeric"
          placeholder="000000"
          value={mfaCode}
          onChange={e => handleMfaChange(e.target.value)}
          disabled={loading}
          style={{
            width: '100%', maxWidth: 240,
            padding: '16px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '2px solid var(--border-strong)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: 32, fontWeight: 700,
            letterSpacing: 12, textAlign: 'center',
            fontFamily: 'monospace',
            outline: 'none',
            marginBottom: 24,
          }}
        />

        <button
          onClick={() => handleVerifyMfa(mfaCode)}
          disabled={loading || mfaCode.length !== 6}
          style={pillBtn({ maxWidth: 300, opacity: (loading || mfaCode.length !== 6) ? 0.5 : 1 })}
        >
          {loading ? 'Verifiserer…' : 'Bekreft'}
        </button>

        {!showRecovery ? (
          <button
            onClick={() => setShowRecovery(true)}
            style={{ marginTop: 24, background: 'none', border: 'none', fontSize: 14, color: 'var(--text3)', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Mistet tilgang til autentiseringsappen?
          </button>
        ) : (
          <div style={{ marginTop: 24, width: '100%', maxWidth: 320, textAlign: 'left', background: 'var(--surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', padding: 16 }}>
            {recoverySent ? (
              <div style={{ color: 'var(--green-dark)', fontSize: 14, lineHeight: 1.6, textAlign: 'center' }}>
                ✓ Gjenopprettingslenke sendt! Sjekk e-posten din.
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.6 }}>
                  Vi sender en gjenopprettingslenke til e-postadressen din. Når du klikker lenken fjernes 2FA og du kan logge inn normalt.
                </p>
                {recoveryError && <ErrorMsg msg={recoveryError} />}
                <button
                  onClick={handleSendRecovery}
                  disabled={recoverySending}
                  style={{ width: '100%', padding: '11px 0', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: recoverySending ? 0.6 : 1 }}
                >
                  {recoverySending ? 'Sender…' : 'Send gjenopprettingslenke'}
                </button>
              </>
            )}
          </div>
        )}

        <button
          onClick={() => { setMode('login'); setMfaCode(''); setError(''); setShowRecovery(false); setRecoverySent(false); }}
          style={{ marginTop: 20, background: 'none', border: 'none', fontSize: 14, color: 'var(--text3)', cursor: 'pointer' }}
        >
          ← Tilbake til innlogging
        </button>
      </div>
    </div>
  );

  if (mode === 'forgot') return (
    <div style={page}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '40px 24px 32px' }}>
        <button
          onClick={() => { setMode('login'); setError(''); setInfo(''); }}
          style={{ background: 'none', border: 'none', padding: 0, width: 36, height: 36, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: 'var(--text2)', marginBottom: 32, cursor: 'pointer' }}
        >
          ←
        </button>
        <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.6, marginBottom: 8, color: 'var(--text)' }}>Glemt passord?</h2>
        <p style={{ fontSize: 15, color: 'var(--text2)', marginBottom: 28, lineHeight: 1.6 }}>
          Skriv inn e-postadressen din så sender vi deg en tilbakestillingslenke.
        </p>
        {error && <ErrorMsg msg={error} />}
        {info && (
          <div style={{ background: 'var(--green-light)', color: 'var(--green-dark)', padding: '12px 14px', borderRadius: 'var(--radius-sm)', fontSize: 14, marginBottom: 14, lineHeight: 1.5 }}>
            {info}
          </div>
        )}
        <Field label="E-post">
          <Input type="email" placeholder="ola@eksempel.no" value={email} onChange={e => setEmail(e.target.value.trim())} autoFocus onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </Field>
        <button onClick={handleSubmit} disabled={loading} style={pillBtn({ opacity: loading ? 0.6 : 1 })}>
          {loading ? 'Sender…' : 'Send tilbakestillingslenke'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={page}>

      {/* Branding */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 24px 40px', textAlign: 'center' }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'var(--green)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 20,
          boxShadow: '0 8px 28px var(--green-glow)',
        }}>
          <Wrench size={34} color="#fff" strokeWidth={2.2} />
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.8, color: 'var(--text)', marginBottom: 7 }}>
          Toolshare.no
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.5 }}>
          Del verktøy med venner og nabolaget
        </p>
      </div>

      {/* Form */}
      <div style={{ padding: '0 20px 48px' }}>
        {recoveryMsg && (
          <div style={{ background: recoveryMsg.ok ? 'var(--green-light)' : 'var(--red-light)', color: recoveryMsg.ok ? 'var(--green-dark)' : 'var(--red)', padding: '12px 14px', borderRadius: 'var(--radius-sm)', marginBottom: 20, fontSize: 14, lineHeight: 1.5 }}>
            {recoveryMsg.ok ? '✓ 2-faktor autentisering er fjernet. Du kan nå logge inn normalt.' : recoveryMsg.msg}
          </div>
        )}
        {hasPendingInvite && (
          <div style={{ background: 'var(--green-light)', color: 'var(--green-dark)', padding: '12px 14px', borderRadius: 'var(--radius-sm)', marginBottom: 20, fontSize: 14, lineHeight: 1.5 }}>
            🔑 Du er invitert — logg inn eller registrer deg for å bli med i gruppen.
          </div>
        )}

        {/* Tab switcher */}
        <div style={{ display: 'flex', background: 'var(--border)', borderRadius: 999, padding: 3, marginBottom: 28 }}>
          {(['login', 'register'] as Mode[]).map(m => (
            <button key={m} onClick={() => { setMode(m); setError(''); setInfo(''); }} style={tabBtn(mode === m)}>
              {m === 'login' ? 'Logg inn' : 'Registrer deg'}
            </button>
          ))}
        </div>

        {error && <ErrorMsg msg={error} />}
        {info && (
          <div style={{ background: 'var(--green-light)', color: 'var(--green-dark)', padding: '10px 13px', borderRadius: 'var(--radius-sm)', fontSize: 14, marginBottom: 14 }}>
            {info}
          </div>
        )}

        {mode === 'register' && (
          <>
            <Field label="Fullt navn">
              <Input placeholder="Ola Nordmann" value={fullName} onChange={e => setFullName(e.target.value)} />
            </Field>
            <Field label="Fylke">
              <Select value={fylke} onChange={e => { setFylke(e.target.value); setKommune(''); }}>
                <option value="">Velg fylke</option>
                {FYLKER.map(f => <option key={f} value={f}>{f}</option>)}
              </Select>
            </Field>
            <Field label="Kommune">
              <Select value={kommune} onChange={e => setKommune(e.target.value)} disabled={!fylke}>
                <option value="">{fylke ? 'Velg kommune' : 'Velg fylke først'}</option>
                {(KOMMUNER_BY_FYLKE[fylke] || []).map(k => <option key={k} value={k}>{k}</option>)}
              </Select>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 5, lineHeight: 1.4 }}>
                Brukes kun til å vise deg relevante, lokale annonser i appen — deles ikke med sponsorer eller andre. Se{' '}
                <button
                  type="button"
                  onClick={() => setShowPersonvern(true)}
                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--green-dark)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Personvern
                </button>
                {' '}for detaljer.
              </div>
            </Field>
          </>
        )}
        <Field label="E-post">
          <Input type="email" placeholder="ola@eksempel.no" value={email} onChange={e => setEmail(e.target.value.trim())} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </Field>
        <Field label="Passord">
          <Input type="password" placeholder={mode === 'register' ? 'Minst 8 tegn med tall og bokstav' : 'Ditt passord'} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </Field>
        {mode === 'register' && (
          <Field label="Bekreft passord">
            <Input type="password" placeholder="Gjenta passordet" value={confirm} onChange={e => setConfirm(e.target.value)} />
          </Field>
        )}

        <button onClick={handleSubmit} disabled={loading} style={pillBtn({ opacity: loading ? 0.6 : 1 })}>
          {loading ? '…' : mode === 'login' ? 'Logg inn' : 'Opprett konto'}
        </button>

        {mode === 'login' && (
          <div style={{ textAlign: 'center', marginTop: 18 }}>
            <button
              onClick={() => { setMode('forgot'); setError(''); setInfo(''); }}
              style={{ background: 'none', border: 'none', fontSize: 14, color: 'var(--text2)', cursor: 'pointer' }}
            >
              Glemt passord?
            </button>
          </div>
        )}
      </div>
      {showPersonvern && <PersonvernPage onClose={() => setShowPersonvern(false)} />}
    </div>
  );
};
